import React from 'react';
import { LuBuilding2, LuCheck, LuCircleX, LuClock, LuEqualApproximately } from 'react-icons/lu';

import { PortfolioEngineAccount, TradingStatus } from '@ndvr/portfolio-api';

import { pluralize } from '@/utils/string-utils';

export type AssessmentFilterKey = 'all' | 'stable' | 'idleCash' | 'insufficientCash' | 'taxLossHarvesting';

interface FilterDef {
    key: AssessmentFilterKey;
    label: string;
    countPhrase: (count: number) => string;
    icon: React.ReactNode;
    iconClassName: string;
    matches: (account: PortfolioEngineAccount) => boolean;
}

const hasAssessmentSignal = (a: PortfolioEngineAccount) => a.tradingStatus !== TradingStatus.REBALANCE_IN_PROGRESS;

const FILTERS: FilterDef[] = [
    {
        key: 'all',
        label: 'All Accounts',
        countPhrase: (c) => pluralize(c, 'account'),
        icon: <LuBuilding2 />,
        iconClassName: 'bg-stone-50 text-neutral-800',
        matches: () => true,
    },
    {
        key: 'stable',
        label: 'Stable',
        countPhrase: (c) => `${c} stable ${c === 1 ? 'account' : 'accounts'}`,
        icon: <LuCheck />,
        iconClassName: 'bg-emerald-50 text-emerald-700',
        matches: (a) => hasAssessmentSignal(a) && !a.hasIdleCash && !a.hasInsufficientCash && !a.hasTaxLossHarvesting,
    },
    {
        key: 'idleCash',
        label: 'Idle Cash',
        countPhrase: (c) => `${pluralize(c, 'account')} with idle cash`,
        icon: <LuClock />,
        iconClassName: 'bg-lime-50 text-green-600',
        matches: (a) => hasAssessmentSignal(a) && a.hasIdleCash,
    },
    {
        key: 'insufficientCash',
        label: 'Insufficient Cash',
        countPhrase: (c) => `${pluralize(c, 'account')} with insufficient cash`,
        icon: <LuCircleX />,
        iconClassName: 'bg-orange-50 text-amber-800',
        matches: (a) => hasAssessmentSignal(a) && a.hasInsufficientCash,
    },
    {
        key: 'taxLossHarvesting',
        label: 'TLH',
        countPhrase: (c) => `${pluralize(c, 'account')} with tax loss harvesting`,
        icon: <LuEqualApproximately />,
        iconClassName: 'bg-blue-50 text-blue-700',
        matches: (a) => hasAssessmentSignal(a) && a.hasTaxLossHarvesting,
    },
];

export const matchesAssessmentFilter = (account: PortfolioEngineAccount, filter: AssessmentFilterKey): boolean =>
    (FILTERS.find((f) => f.key === filter) ?? FILTERS[0]).matches(account);

export const formatAssessmentFilterCount = (filter: AssessmentFilterKey, count: number): string =>
    (FILTERS.find((f) => f.key === filter) ?? FILTERS[0]).countPhrase(count);

export const AssessmentFilter = ({
    accounts,
    value,
    onChange,
}: {
    accounts: PortfolioEngineAccount[];
    value: AssessmentFilterKey;
    onChange: (filter: AssessmentFilterKey) => void;
}) => (
    <div
        className="-mx-4 overflow-x-auto px-4"
        style={{
            maskImage:
                'linear-gradient(to right, transparent 0, black 1rem, black calc(100% - 1rem), transparent 100%)',
            WebkitMaskImage:
                'linear-gradient(to right, transparent 0, black 1rem, black calc(100% - 1rem), transparent 100%)',
        }}
    >
        <div className="flex w-max gap-2">
            {FILTERS.map((filter) => {
                const count = accounts.filter(filter.matches).length;
                const selected = filter.key === value;
                return (
                    <button
                        key={filter.key}
                        type="button"
                        onClick={() => onChange(filter.key)}
                        aria-pressed={selected}
                        className={`flex shrink-0 items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${
                            selected
                                ? 'border-neutral-800 bg-white shadow-sm'
                                : 'border-neutral-300 bg-white hover:border-neutral-500'
                        }`}
                    >
                        <span
                            className={`inline-flex h-9 w-9 items-center justify-center rounded-md text-base ${filter.iconClassName}`}
                        >
                            {filter.icon}
                        </span>
                        <span className="flex flex-col leading-tight">
                            <span
                                className={`text-sm font-semibold ${selected ? 'text-neutral-950' : 'text-neutral-700'}`}
                            >
                                {filter.label}
                            </span>
                            <span className="text-xs font-normal text-neutral-600">{count}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    </div>
);
